/**
 * useMutation hooks for StoryView write operations.
 * Calls the backend via `transport` + `routes/hasura`; cache updates in `onSuccess`.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { transport } from '@opengov/app-core/transport';
import {
  TAB_PUBLICATIONS,
  FLAG_ADD_PROPOSALS_TO_STORIES,
  DATE_FORMAT_WITH_TIMEZONE,
  STORIES_EDITOR_PRIVILEGE,
  STORIES_PUBLISHER_PRIVILEGE
} from '@opengov/stories-shared/constants';
import {
  filterAndOrderStoryContents,
  disableClickFromStoryContentWithEmbedV3,
  fetchProgressTrackerSettings,
  generateBase64HTML,
  orderStoryContents,
  sortStoryContents
} from '@opengov/stories-shared/utils';
import { replaceStoryContentsWithMergeTagValues } from '@opengov/stories-shared/utils/mergeTag';
import { downloadPdfFromBlob } from '../../../utils/StoryContentsFrame/story.utils';
import { useFeatureFlagStore } from '@opengov/feature-flag/launchDarkly';
import { SegmentEffects } from '@opengov/app-core/analytics';
import { refreshEmbed } from '@opengov/reporting-embeds';
import { isEmpty, unionBy } from 'lodash';
import moment from 'moment';
import * as qs from 'query-string';

import {
  removeStory,
  removeTemplate,
  updateStoryContent as updateStoryContentQuery,
  updateTemplateContent,
  updateStoryUpdatedAt,
  updateStoryCustomColors as updateStoryCustomColorsQuery,
  updateTemplateCustomColors as updateTemplateCustomColorsQuery,
  deleteStoryEmbed as deleteStoryEmbedQuery
} from '../../../graphQL/queries';
import {
  processStoryContentOrTemplateContent,
  addProposalToStory as addProposalToStoryGql,
  removeBudgetAndProposalsInStory as removeBudgetAndProposalsInStoryGql,
  removeAllBudgetProposalsInStory as removeAllBudgetProposalsInStoryGql,
  replaceProposalInStory as replaceProposalInStoryGql,
  updatePublicationUpdatedAt as updatePublicationUpdatedAtGql,
  upsertStoryProgressTrackerConfig as upsertStoryProgressTrackerConfigGql,
  getStoryContentsById,
  getTemplateContentsById
} from '@opengov/stories-shared/graphQL';
import { applyBrandingSettings, updateStoryContentId, updateContentTileMedia } from '../../../utils';
import { getMediaBlockIndexFromId } from '../../../utils/mediaIndex.utils';
import { getEmbedMediaBlockAttributes } from '../../../utils/dom';
import {
  createTemplateFromStory,
  deleteStoryContent as deleteStoryContentRemote,
  getUserById,
  insertEmbeds,
  removeEmbeds,
  updateStoryOrTemplate,
  publishInternalStory,
  publishExternalStory,
  unpublishInternalStory,
  unpublishExternalStory,
  PublishStoryParams
} from '../../../routes/hasura';
import { ensureSettings } from '../../../adapter/stories-hasura-service/api';
import { processEntitySettings } from '../../../transformations/settingsTransformation';
import {
  applyTemplateReplacement,
  mediaDivTag,
  mediaDivV3Tag,
  mediaScriptTag,
  mediaScriptV3Tag
} from '../../../common/utils';
import { EMBED_REQUEST_TYPE, EMBED_SYNC_SETTING, MEDIA_TABLE_TILE, MEDIA_TILE, TAG_SCRIPT } from '../../../constants';
import { OpenGovAppConfig } from '@opengov/app-config';
import {
  navigateToFolderList,
  navigateToPublicationFolderList,
  navigateToPublicationList,
  navigateToPublicationStoryView,
  navigateToPublicationView,
  navigateToStoryEdit,
  navigateToStoryList,
  navigateToStoryView,
} from '../../../state/navigation';
import { useToast } from '../../Toast/context/useToast.context';
import { storyViewQueryKeys } from './storyViewQueryKeys';
import { publicationViewQueryKeys } from '../../Publications/PublicationView/hooks/publicationViewQueryKeys';
import {
  IExportStoryProps,
  IPreviewPdfProps,
  IUpdateCustomColorProps,
  IUpdateStoryContentProps,
  IUpdateStoryProps
} from '../../../../types/storyViewTypes';
import {
  IMergeTags,
  IPublication,
  IStory,
  IStoryContent,
  ProgressTrackerSettingType
} from '@opengov/stories-shared/types';
import { IExtendedStoryPrivilege } from '../../../../types/claimTypes';
import {
  StoryMergeTagsOperation,
  MergeTagsOperation,
  removeStoryMergeTags,
  updateStoryMergeTags,
  replaceStoryMergeTags,
} from '../../../common/context/MergeTagsContext';

const operateUrl = OpenGovAppConfig.OPERATE_URL;

// Shared media-tile object shape produced while rebuilding embed markup.
type ITileMediaObject = {
  mediaIndex: number;
  mediaDiv: string;
  mediaScript: string;
};

// Input for useCopyToTemplate (previously imported from the retired effects module).
export type CopyToTemplateParams = {
  entityId: number;
  entitySubdomain: string;
  userId: number;
  title: string;
  description: string;
  storyContents: IStoryContent[];
  messages: {
    storyCopiedToTemplates: string;
    templateSaved: string;
    templateNotSaved: string;
    storyNotCopiedToTemplates: string;
    [key: string]: string;
  };
  settings: any;
  paddingTop: number;
  paddingBottom: number;
  customCssCode?: string;
};

// ---------------------------------------------------------------------------
// Module-scoped transport helpers (inlined from the retired compose-app
// effects, minus the Redux dispatches those effects performed).
// ---------------------------------------------------------------------------

/** Fetch + sort story/template contents (no compose-app dispatch). */
async function fetchStoryContents({
  isTemplate,
  entityId,
  storyId,
}: {
  isTemplate: boolean;
  entityId: number;
  storyId: string;
}): Promise<IStoryContent[]> {
  let variables: { [key: string]: string | number } = {
    [isTemplate ? 'storyTemplateId' : 'storyId']: storyId,
    limit: 100
  };
  if (!isTemplate) {
    variables = { ...variables, entityId };
  }
  const response = await transport
    .url('/api/data/objects/stories/v1/graphql')
    .json({ query: isTemplate ? getTemplateContentsById : getStoryContentsById, variables })
    .post()
    .json();
  if (!response.data) return Promise.reject(response);
  const storyContents: IStoryContent[] = response.data.storyContents.map(processStoryContentOrTemplateContent);
  return orderStoryContents(sortStoryContents(storyContents));
}

/** Persist a story's embed list: remove all, then re-insert (simple branch). */
async function persistStoryEmbeds(story: IStory, storyContents: IStoryContent[]): Promise<void> {
  const { _id } = story;
  const currentEmbeds: { id: string }[] = [];
  await Promise.all(
    storyContents.map(async (storyContent) => {
      const { content } = storyContent;
      const fragment = document.createRange().createContextualFragment(content);
      const mediaBlocks: Element[] = Array.prototype.slice.call(fragment.querySelectorAll('.media-block'));
      for (const mediaBlock of mediaBlocks) {
        for (let i = 0; i < mediaBlock.children.length; i++) {
          if (
            mediaBlock.children[i].tagName === TAG_SCRIPT &&
            mediaBlock.children[i].hasAttribute('data-embed-id')
          ) {
            const embedId = mediaBlock.children[i].getAttribute('data-embed-id') as string;
            currentEmbeds.push({ id: embedId });
          }
        }
      }
    })
  );
  const variables = currentEmbeds.map(({ id }) => ({ storyId: _id, embedId: id }));
  await removeEmbeds(_id);
  await insertEmbeds(variables);
}

// ---------------------------------------------------------------------------
// useUpdateStory
// isSaving source: mutation.isLoading
// ---------------------------------------------------------------------------

export function useUpdateStory() {
  const queryClient = useQueryClient();
  return useMutation<IStory, Error, IUpdateStoryProps>({
    mutationFn: async (props) => {
      const {
        isTemplate,
        isAdmin,
        shouldRedirect,
        entitySubdomain,
        story: { _id, state, visibility, title, description, embeds, paddingTop, paddingBottom, isFollowable },
        embed,
        generateThumbnail = true,
        publicationId = '',
        currentSubjectPublicationStoryClaim
      } = props;

      const authorized =
        isAdmin ||
        (Array.isArray(currentSubjectPublicationStoryClaim) &&
          currentSubjectPublicationStoryClaim.includes(STORIES_EDITOR_PRIVILEGE));
      if (!authorized) {
        throw new Error('Unauthorized');
      }

      const embedsArray = embeds;
      const variables: Partial<IStory> = {
        title,
        description,
        state,
        visibility,
        updatedAt: new Date().toISOString(),
        paddingTop,
        paddingBottom,
        isFollowable
      };
      if (!isEmpty(embed)) {
        embedsArray?.push(embed as any);
      }

      const remoteServiceResponse = updateStoryOrTemplate(isTemplate, _id, variables);

      if (generateThumbnail) {
        // Fire-and-forget thumbnail regeneration; don't block the save.
        void transport
          .url(`/api/stories/v1/stories/${_id}/thumbnail?template=${isTemplate}&entitySubdomain=${entitySubdomain}`)
          .get();
      }

      const data = await remoteServiceResponse;
      if (shouldRedirect) {
        void SegmentEffects.track({ eventName: 'Stories: Story: Save and Exit', props: data }).catch(() => null);
        publicationId
          ? navigateToPublicationStoryView(entitySubdomain, _id, isTemplate, publicationId)
          : navigateToStoryView(entitySubdomain, _id, isTemplate);
      }
      return data;
    },
    onSuccess: (story, variables) => {
      if (story?._id) {
        queryClient.setQueryData(
          storyViewQueryKeys.story({ storyId: story._id, isTemplate: variables.isTemplate }),
          story
        );
      }
      // Invalidate publication list/hierarchy so saved changes show on next mount.
      void queryClient.invalidateQueries({ queryKey: [...publicationViewQueryKeys.all, 'stories'] });
      void queryClient.invalidateQueries({ queryKey: [...publicationViewQueryKeys.all, 'foldersHierarchy'] });
    }
  });
}

// ---------------------------------------------------------------------------
// usePublishStory
// isPublishing source: mutation.isLoading
// ---------------------------------------------------------------------------

export type PublishStoryInput = {
  isTemplate: boolean;
  isAdmin: boolean;
  hasGallery: boolean;
  entitySubdomain: string;
  story: IStory;
  userId: number;
  isInternal?: boolean;
  entityUuid?: string;
  storiesAccessControlEnabled?: boolean;
  currentSubjectPublicationStoryClaim?: IExtendedStoryPrivilege | null;
  progressTrackerSettings?: ProgressTrackerSettingType[];
};

export function usePublishStory() {
  const queryClient = useQueryClient();
  return useMutation<IStory, Error, PublishStoryInput>({
    mutationFn: async ({
      entitySubdomain,
      isAdmin,
      isInternal,
      userId,
      isTemplate,
      hasGallery,
      story,
      entityUuid = '',
      currentSubjectPublicationStoryClaim,
      progressTrackerSettings
    }) => {
      const { _id } = story;
      const authorized =
        isAdmin ||
        (Array.isArray(currentSubjectPublicationStoryClaim) &&
          currentSubjectPublicationStoryClaim.includes(STORIES_PUBLISHER_PRIVILEGE));
      if (!authorized) {
        throw new Error('Unauthorized');
      }

      // Fire-and-forget thumbnail regeneration.
      void transport.url(`/api/stories/v1/stories/${_id}/thumbnail?entitySubdomain=${entitySubdomain}`).get();

      const sortedStoryContents = await fetchStoryContents({
        isTemplate,
        entityId: story.entity_id,
        storyId: story._id
      });
      const settings = processEntitySettings(await ensureSettings(story.entity_id));

      const progressTrackerSettingsData =
        progressTrackerSettings || (story.entity_id ? await fetchProgressTrackerSettings(story.entity_id) : []);

      const publishStoryParams: PublishStoryParams = {
        sortedStoryContents,
        settings,
        isTemplate,
        hasGallery,
        userId,
        story,
        storyCustomCss: story.customCssCode,
        publicationCustomCss: story.folder?.publication?.customCssCode,
        progressTrackerSettings: progressTrackerSettingsData
      };

      const response: IStory = isInternal
        ? await publishInternalStory(publishStoryParams)
        : await publishExternalStory({ ...publishStoryParams, entityUuid });

      if (response.state === 'published') {
        void SegmentEffects.track({ eventName: 'Stories: Story: Published Externally', props: response }).catch(
          () => null
        );
      } else if (response.state === 'internalPublished') {
        void SegmentEffects.track({ eventName: 'Stories: Story: Published Internally', props: response }).catch(
          () => null
        );
      }

      const publicationId = story.folder?.publicationId;
      if (publicationId) {
        navigateToPublicationStoryView(entitySubdomain, _id, isTemplate, publicationId);
      } else {
        navigateToStoryView(entitySubdomain, _id, isTemplate);
      }
      return response;
    },
    onSuccess: (story, variables) => {
      if (story?._id) {
        queryClient.setQueryData(
          storyViewQueryKeys.story({ storyId: story._id, isTemplate: variables.isTemplate }),
          story
        );
      }
      // Publish flips the story's state badge in the Publication View list — refetch it.
      void queryClient.invalidateQueries({ queryKey: [...publicationViewQueryKeys.all, 'stories'] });
      void queryClient.invalidateQueries({ queryKey: [...publicationViewQueryKeys.all, 'foldersHierarchy'] });
    }
  });
}

// ---------------------------------------------------------------------------
// useUnpublishStory
// ---------------------------------------------------------------------------

export type UnpublishStoryInput = {
  isAdmin: boolean;
  isTemplate: boolean;
  entitySubdomain: string;
  redirectToEdit: boolean;
  story: IStory;
  storiesAccessControlEnabled?: boolean;
  currentSubjectPublicationStoryClaim?: IExtendedStoryPrivilege | null;
};

export function useUnpublishStory() {
  const queryClient = useQueryClient();
  return useMutation<IStory, Error, UnpublishStoryInput>({
    mutationFn: async ({
      isAdmin,
      redirectToEdit,
      entitySubdomain,
      story,
      currentSubjectPublicationStoryClaim,
      storiesAccessControlEnabled
    }) => {
      const { _id, state } = story;
      const isInternal = state === 'internalPublished';
      const authorized =
        isAdmin ||
        (storiesAccessControlEnabled &&
          Array.isArray(currentSubjectPublicationStoryClaim) &&
          currentSubjectPublicationStoryClaim.includes(STORIES_PUBLISHER_PRIVILEGE));
      if (!authorized) {
        throw new Error('Unauthorized');
      }

      const response: IStory = isInternal
        ? await unpublishInternalStory(story)
        : await unpublishExternalStory(story);

      if (redirectToEdit) {
        navigateToStoryEdit(entitySubdomain, _id);
      }
      return response;
    },
    onSuccess: (story, variables) => {
      if (story?._id) {
        queryClient.setQueryData(
          storyViewQueryKeys.story({ storyId: story._id, isTemplate: variables.isTemplate }),
          story
        );
      }
      // Unpublish flips the story's state badge in the Publication View list — refetch it.
      void queryClient.invalidateQueries({ queryKey: [...publicationViewQueryKeys.all, 'stories'] });
      void queryClient.invalidateQueries({ queryKey: [...publicationViewQueryKeys.all, 'foldersHierarchy'] });
    }
  });
}

// ---------------------------------------------------------------------------
// usePreviewPdf
// isPreviewingPdf source: mutation.isLoading
// ---------------------------------------------------------------------------

export function usePreviewPdf() {
  return useMutation<string, Error, IPreviewPdfProps>({
    mutationFn: async (props) => {
      const {
        displayDate,
        displayPageNumber,
        isTemplate,
        displayTitle,
        entitySubdomain,
        enlargeFonts,
        startingPage,
        story: { _id },
        storyMarginsEnabled,
        controller,
        margins,
        showHeader,
        showFooter,
        isExportFromSetupPdf = false,
        displayDummyPageNumber = false,
        watermark
      } = props;

      const query = qs.stringify(
        {
          displayPageNumber,
          displayTitle,
          displayDate,
          enlargeFonts,
          template: isTemplate,
          showMargins: !storyMarginsEnabled,
          entitySubdomain,
          startingPage,
          margins: encodeURIComponent(JSON.stringify(margins)),
          showHeader,
          showFooter,
          isExportFromSetupPdf,
          watermark,
          displayDummyPageNumber
        },
        { encode: false }
      );

      /*
       * The `showMargins` query param tells the content export service to include Puppeteer margins. This is something we
       * turn off when the `product-stories-feature-story-margins` feature is enabled because margins are handled in a
       * different way.
       */
      try {
        const link = await transport
          .url(`/api/stories/v1/stories/${_id}/pdf?${query}`)
          .signal(controller)
          .get()
          .json<{ pdf?: string }>();
        return link?.pdf ?? '';
      } catch (error: any) {
        /*
         * Don't surface errors for aborted requests — subsequent form changes generate new requests that abort the
         * in-flight one.
         */
        if (error && error.name !== 'AbortError') {
          throw error;
        }
        return '';
      }
    }
  });
}

// ---------------------------------------------------------------------------
// useUpdateStoryCustomColors
// ---------------------------------------------------------------------------

export function useUpdateStoryCustomColors() {
  const queryClient = useQueryClient();
  return useMutation<unknown, Error, IUpdateCustomColorProps>({
    mutationFn: async ({ customColors, story, isTemplate }) => {
      const customColorsArray: Record<string, unknown>[] = [];
      let key: string;
      let value: any;
      for ([key, value] of Object.entries(customColors)) {
        value = value || {};
        for (const [target, colors] of Object.entries(value)) {
          if (target === 'background' || target === 'font') {
            customColorsArray.push({
              contentType: key === 'content' ? 'body_cotent' : key,
              colorTarget: target,
              colors,
              ...(isTemplate ? { templateId: story._id } : { storyId: story._id })
            });
          }
        }
      }
      const response = await transport
        .url('/api/data/objects/stories/v1/graphql')
        .json({
          query: isTemplate ? updateTemplateCustomColorsQuery : updateStoryCustomColorsQuery,
          variables: { customColors: customColorsArray }
        })
        .post()
        .json();
      if (!response.data) return Promise.reject(response.errors);
      return response;
    },
    onSuccess: (_, variables) => {
      const { story, isTemplate, customColors } = variables;
      if (!story?._id) return;
      const key = storyViewQueryKeys.story({ storyId: story._id, isTemplate });
      const cached = queryClient.getQueryData<IStory>(key);
      if (cached) {
        queryClient.setQueryData(key, {
          ...cached,
          meta: { ...(cached.meta ?? {}), customColors }
        });
      }
    }
  });
}

// ---------------------------------------------------------------------------
// postStoryContentUpdate — single-content GraphQL save (inlined transport).
// Shared by useUpdateStoryContentDB, useUpdateAllStoryContentsDB,
// useUpdateAllStoryContentsMergeTagsDB and useRefreshEmbeds. Cache writes are
// reflected by each hook's onSuccess via setQueryData.
// ---------------------------------------------------------------------------

async function postStoryContentUpdate({
  isTemplate,
  story,
  storyContent,
  shouldApplyBranding,
  settings,
  entityName,
  controller,
}: IUpdateStoryContentProps): Promise<IStoryContent> {
  const sc = storyContent as IStoryContent;
  let storyContentData: any = {
    _id: sc._id,
    [isTemplate ? 'storyTemplateId' : 'storyId']: story._id,
    title: sc.title,
    description: sc.description,
    content: sc.content,
    contentHtml: sc.content,
    group: sc.group,
    format: sc.format,
    type: sc.type,
    order: sc.order,
    active: sc.active,
    entityId: sc.entity_id,
    authorId: sc.author._id,
  };

  if (shouldApplyBranding && settings && entityName) {
    const branded = applyBrandingSettings(sc, settings, entityName);
    storyContentData = { ...storyContentData, content: branded.content, contentHtml: branded.content };
  }

  const abortController = controller ?? new AbortController();
  const response = await transport
    .url('/api/data/objects/stories/v1/graphql')
    .json({ query: isTemplate ? updateTemplateContent : updateStoryContentQuery, variables: { storyContentData } })
    .signal(abortController)
    .post()
    .json();

  if (!response.data) return Promise.reject(response.errors ?? response);

  if (!isTemplate) {
    // Bump the story's updatedAt with the current date/time so autosave and
    // add-section saves reflect a fresh timestamp (fire-and-forget).
    void transport
      .url('/api/data/objects/stories/v1/graphql')
      .json({ query: updateStoryUpdatedAt, variables: { id: story._id, updatedAt: new Date() } })
      .post()
      .res()
      .catch(() => undefined);
  }

  return processStoryContentOrTemplateContent(response.data.contents) as IStoryContent;
}

// ---------------------------------------------------------------------------
// useUpdateStoryContentDB
// ---------------------------------------------------------------------------

export function useUpdateStoryContentDB() {
  const queryClient = useQueryClient();
  return useMutation<IStoryContent, Error, IUpdateStoryContentProps>({
    mutationFn: (props) => postStoryContentUpdate(props),
    onSuccess: (result, { story, isTemplate, storyContent }) => {
      const entityId = (storyContent as IStoryContent).entity_id as number;
      queryClient.setQueryData<IStoryContent[]>(
        storyViewQueryKeys.storyContents({ storyId: story._id, isTemplate, entityId }),
        // Keep existing shouldUpdate; do not force a re-seed after autosave.
        (old = []) => old.map((sc) => (sc._id === result._id ? { ...result, shouldUpdate: sc.shouldUpdate } : sc))
      );
    },
  });
}

// ---------------------------------------------------------------------------
// useUpdateAllStoryContentsDB
// ---------------------------------------------------------------------------

export type UpdateAllStoryContentsInput = {
  isTemplate: boolean;
  story: IStory;
  storyContents: IStoryContent[];
  settings?: any;
  entityName?: string;
  shouldApplyBranding?: boolean;
};

export function useUpdateAllStoryContentsDB() {
  const queryClient = useQueryClient();
  return useMutation<IStoryContent[], Error, UpdateAllStoryContentsInput>({
    mutationFn: ({ isTemplate, story, storyContents, settings, entityName, shouldApplyBranding }) =>
      Promise.all(
        storyContents.map((storyContent) =>
          postStoryContentUpdate({ isTemplate, story, storyContent, settings, entityName, shouldApplyBranding })
        )
      ),
    onSuccess: (results, { story, isTemplate }) => {
      if (!results.length) return;
      const entityId = results[0].entity_id as number;
      queryClient.setQueryData<IStoryContent[]>(
        storyViewQueryKeys.storyContents({ storyId: story._id, isTemplate, entityId }),
        (old = []) =>
          old.map((sc) => {
            const updated = results.find((r) => r._id === sc._id);
            return updated ? { ...updated, shouldUpdate: true } : sc;
          })
      );
    },
  });
}

// ---------------------------------------------------------------------------
// useUpdateAllStoryContentsMergeTagsDB
// ---------------------------------------------------------------------------

export type UpdateAllStoryContentsMergeTagsInput = {
  isTemplate: boolean;
  story: IStory;
  storyContents: IStoryContent[];
  publicationId: string;
  userId: number;
  storyMergeTagsOperation: StoryMergeTagsOperation;
};

export function useUpdateAllStoryContentsMergeTagsDB() {
  const queryClient = useQueryClient();
  return useMutation<IStoryContent[], Error, UpdateAllStoryContentsMergeTagsInput>({
    mutationFn: async ({ isTemplate, story, storyContents, publicationId, userId, storyMergeTagsOperation }) => {
      let updatedStoryContents = storyContents;
      switch (storyMergeTagsOperation.operation) {
        case MergeTagsOperation.Remove:
          updatedStoryContents = removeStoryMergeTags(storyContents, storyMergeTagsOperation);
          break;
        case MergeTagsOperation.Update:
          updatedStoryContents = updateStoryMergeTags(storyContents, storyMergeTagsOperation);
          break;
        case MergeTagsOperation.Replace:
          updatedStoryContents = replaceStoryMergeTags(storyContents, storyMergeTagsOperation);
          break;
        default:
          break;
      }

      const results = await Promise.all(
        updatedStoryContents.map((storyContent) => postStoryContentUpdate({ isTemplate, story, storyContent }))
      );

      // Bump publication updatedAt; thumbnail refresh is caller's job.
      if (publicationId && userId) {
        const authorId = await getUserById(userId);
        const formattedDate = moment(new Date()).format(DATE_FORMAT_WITH_TIMEZONE);
        await transport
          .url('/api/data/objects/stories/v1/graphql')
          .json({
            query: updatePublicationUpdatedAtGql,
            variables: { publicationId, updatedAt: formattedDate, userId: authorId }
          })
          .post()
          .json();
      }

      return results;
    },
    onSuccess: (results, { story, isTemplate }) => {
      if (!results.length) return;
      const entityId = results[0].entity_id as number;
      queryClient.setQueryData<IStoryContent[]>(
        storyViewQueryKeys.storyContents({ storyId: story._id, isTemplate, entityId }),
        (old = []) =>
          old.map((sc) => {
            const updated = results.find((r) => r._id === sc._id);
            return updated ? { ...updated, shouldUpdate: true } : sc;
          })
      );
    },
  });
}

// ---------------------------------------------------------------------------
// useRefreshEmbeds
// ---------------------------------------------------------------------------

export type RefreshEmbedsInput = {
  isTemplate: boolean;
  isAdmin: boolean;
  hasGallery: boolean;
  entitySubdomain: string;
  settings: any;
  story: IStory;
  storyContents: IStoryContent[];
  messages: any;
};

export function useRefreshEmbeds() {
  const { toggleToast } = useToast();
  const queryClient = useQueryClient();

  return useMutation<IStoryContent[], Error, RefreshEmbedsInput>({
    mutationFn: async ({ isTemplate, story, storyContents, messages }) => {
      const sortedStoryContents = orderStoryContents(sortStoryContents(storyContents));
      const embeds = (story.embeds || []) as any[];
      const embedsToKeep: string[] = [];
      const embedsToRemove: string[] = [];
      const updatedStoryContents: IStoryContent[] = [];

      await Promise.all(
        sortedStoryContents.map(async (storyContent) => {
          const { content } = storyContent;
          const mediaToAdd: ITileMediaObject[] = [];
          let embedRequestType = '';
          const fragment = document.createRange().createContextualFragment(content);
          const mediaBlocks: Element[] = Array.prototype.slice.call(fragment.querySelectorAll('.media-block'));
          for (const mediaBlock of mediaBlocks) {
            const mediaIndexFromId = getMediaBlockIndexFromId(mediaBlock.id);
            const mediaIndexAttr = parseInt(mediaBlock.getAttribute('data-media-index') as string, 10);
            const mediaIndexFromAttr = mediaIndexAttr ? mediaIndexAttr - 1 : 0;
            const mediaIndex = mediaIndexFromAttr || mediaIndexFromId || 0;

            const embedAttributes = getEmbedMediaBlockAttributes(mediaBlock);
            const embedId = embedAttributes.embedId;
            const divId = embedAttributes.divId;
            const updateSetting = embedAttributes.updateSetting;
            const disableClick = embedAttributes.disableClick;
            const showReportLink = embedAttributes.showReportLink;
            const displayAccountCodes = embedAttributes.displayAccountCodes;
            const embedTitle = embedAttributes.embedTitle;
            const embedVersion = embedAttributes.embedVersion;
            if (updateSetting !== EMBED_SYNC_SETTING.REFRESH_ON_UPDATE) {
              embedsToKeep.push(embedId);
            } else {
              embedsToRemove.push(embedId);
              let refreshedEmbed;
              try {
                refreshedEmbed = await refreshEmbed(embedId, embedVersion);
              } catch {
                continue;
              }
              if (!refreshedEmbed) {
                continue;
              }

              if (embedVersion === 'v3' || embedVersion === 'v4') {
                embeds.push(refreshedEmbed.id);
                embedRequestType = refreshedEmbed.type;
                const mediaDivReplacements = {
                  disableClick,
                  uniqueId: refreshedEmbed.id,
                  updateSetting,
                  embedId: refreshedEmbed.id,
                  privacyTag: !(
                    refreshedEmbed.publicUrl ||
                    refreshedEmbed.data?.publicUrl ||
                    refreshedEmbed.data?.tile?.publicUrl
                  )
                    ? `data-warning="${messages.tileWarning}"`
                    : '',
                  version: refreshedEmbed.version
                };
                const mediaDiv = applyTemplateReplacement(mediaDivTag, mediaDivReplacements);
                const mediaScriptReplacements = {
                  disableClick,
                  uniqueId: refreshedEmbed.id,
                  operateUrl,
                  embedId: refreshedEmbed.id,
                  version: refreshedEmbed.version
                };
                const mediaScript = applyTemplateReplacement(mediaScriptTag, mediaScriptReplacements);
                mediaToAdd.push({ mediaIndex, mediaDiv, mediaScript });
              } else {
                embeds.push(refreshedEmbed.id);
                embedRequestType = refreshedEmbed.type;
                const { id } = refreshedEmbed;
                const publicUrl =
                  refreshedEmbed.publicUrl ||
                  refreshedEmbed.data?.publicUrl ||
                  refreshedEmbed.data?.tile?.publicUrl;
                const mediaDivV3Replacement = {
                  uniqueId: divId,
                  privacyTag: !publicUrl ? ` data-warning="${messages.tileWarning}"` : '',
                  updateSetting,
                  disableClick,
                  showReportLink,
                  displayAccountCodes,
                  embedTitle,
                  embedId: id
                };
                const mediaScriptV3Replacements = {
                  disableClick,
                  divId,
                  operateUrl,
                  embedId: id,
                  version: 'v2',
                  showReportLink,
                  displayAccountCodes,
                  embedTitle
                };
                const mediaDiv = applyTemplateReplacement(mediaDivV3Tag, mediaDivV3Replacement);
                const mediaScript = applyTemplateReplacement(mediaScriptV3Tag, mediaScriptV3Replacements);
                mediaToAdd.push({ mediaIndex, mediaDiv, mediaScript });
              }
            }
          }
          if (!isEmpty(mediaToAdd) && mediaBlocks.length > 0) {
            const embedMediaType =
              embedRequestType === EMBED_REQUEST_TYPE.SummaryTable ||
                embedRequestType === EMBED_REQUEST_TYPE.DetailTable
                ? MEDIA_TABLE_TILE
                : MEDIA_TILE;
            const newStoryContent = updateContentTileMedia(storyContent, mediaToAdd, mediaToAdd.length, embedMediaType);
            const savedStoryContent = await postStoryContentUpdate({
              isTemplate,
              story,
              storyContent: newStoryContent
            });
            updatedStoryContents.push(savedStoryContent ?? newStoryContent);
          }
        })
      );

      const newEmbeds = embeds.filter(
        (id: string) => !embedsToRemove.includes(id) || embedsToKeep.includes(id)
      );
      await persistStoryEmbeds(
        { ...story, embeds: newEmbeds } as IStory,
        unionBy(updatedStoryContents, sortedStoryContents, '_id')
      );
      // Keep the cached story's embed list in step with what was just persisted.
      queryClient.setQueryData<IStory>(
        storyViewQueryKeys.story({ storyId: story._id, isTemplate }),
        (prev) => (prev ? ({ ...prev, embeds: newEmbeds } as IStory) : prev)
      );
      toggleToast({
        title: messages.embedsUpdated,
        body: messages.embedDataRefreshed,
        severity: 'success'
      });
      return updatedStoryContents;
    },
    onSuccess: (updatedStoryContents, { isTemplate, story }) => {
      if (!updatedStoryContents.length) return;
      const entityId = updatedStoryContents[0].entity_id as number;
      queryClient.setQueryData<IStoryContent[]>(
        storyViewQueryKeys.storyContents({ storyId: story._id, isTemplate, entityId }),
        (old = []) =>
          old.map((sc) => {
            const refreshed = updatedStoryContents.find((r) => r._id === sc._id);
            // shouldUpdate re-seeds the editor for that section, which remounts the
            // embed <script> against the new embed id so the tile re-renders in place.
            return refreshed ? { ...refreshed, shouldUpdate: true } : sc;
          })
      );
    },
    onError: (_error, { messages }) => {
      toggleToast({
        title: messages.somethingWentWrong,
        body: messages.errorEmbedAddUpdate,
        severity: 'error'
      });
    }
  });
}

// ---------------------------------------------------------------------------
// useDeleteStoryContent
// ---------------------------------------------------------------------------

export type DeleteStoryContentInput = {
  isTemplate: boolean;
  entityId: number;
  entitySubdomain: string;
  story: IStory;
  storyContentId: string;
  embedIds?: string[];
};

export function useDeleteStoryContent() {
  const queryClient = useQueryClient();
  return useMutation<unknown, Error, DeleteStoryContentInput>({
    mutationFn: async ({ isTemplate, story, storyContentId, embedIds }) => {
      const res = await deleteStoryContentRemote(isTemplate, storyContentId);
      // Delete embed(s) from the storyEmbeds table (fire-and-forget, per effect).
      if (Array.isArray(embedIds) && embedIds.length > 0) {
        void Promise.all(
          embedIds.map((embedId) =>
            transport
              .url('/api/data/objects/stories/v1/graphql')
              .json({ query: deleteStoryEmbedQuery, variables: { storyId: story._id, embedId } })
              .post()
              .json()
              .catch(() => null)
          )
        );
      }
      return res;
    },
    onSuccess: (_data, { story, isTemplate, entityId }) => {
      void queryClient.invalidateQueries({
        queryKey: storyViewQueryKeys.storyContents({ storyId: story._id, isTemplate, entityId })
      });
    }
  });
}

// ---------------------------------------------------------------------------
// useDeleteStoryEmbed
// ---------------------------------------------------------------------------

export type DeleteStoryEmbedInput = {
  storyId: string;
  embedId: string;
};

export function useDeleteStoryEmbed() {
  return useMutation<unknown, Error, DeleteStoryEmbedInput>({
    mutationFn: async ({ storyId, embedId }) => {
      const abortController = new AbortController();
      const response = await transport
        .url('/api/data/objects/stories/v1/graphql')
        .json({ query: deleteStoryEmbedQuery, variables: { storyId, embedId } })
        .signal(abortController)
        .post()
        .json();
      if (response.errors) {
        return Promise.reject(response.errors);
      }
      return response;
    }
  });
}

// ---------------------------------------------------------------------------
// useSaveProgressTrackerConfig
// ---------------------------------------------------------------------------

export type SaveProgressTrackerConfigInput = {
  storyId: string;
  contentId: string;
  entityId: number;
  id?: string;
  config: {
    title: string;
    showPreview: boolean;
    events: { title: string; description: string; status: string }[];
  };
};

export function useSaveProgressTrackerConfig() {
  const queryClient = useQueryClient();
  return useMutation<IStoryContent[], Error, SaveProgressTrackerConfigInput>({
    mutationFn: async ({ storyId, contentId, entityId, config, id }) => {
      const progressTrackerPayload = {
        payload: {
          storyId,
          contentId,
          entityId,
          type: 'timeline',
          config,
          ...(id && { id })
        }
      };
      const response = await transport
        .url('/api/data/objects/stories/v1/graphql')
        .json({ query: upsertStoryProgressTrackerConfigGql, variables: progressTrackerPayload })
        .post()
        .json();
      if (!response.data) return Promise.reject(response.errors ?? response);
      // Return the refreshed contents so callers can update their view.
      return fetchStoryContents({ isTemplate: false, entityId, storyId });
    },
    onSuccess: (freshContents, { storyId, entityId }) => {
      queryClient.setQueryData<IStoryContent[]>(
        storyViewQueryKeys.storyContents({ storyId, isTemplate: false, entityId }),
        (old) => {
          if (!old) return freshContents;
          return old.map((storyContent) => {
            const fresh = freshContents.find((content) => content._id === storyContent._id);
            return fresh ? { ...storyContent, contentChartConfig: fresh.contentChartConfig } : storyContent;
          });
        }
      );
    }
  });
}

// ---------------------------------------------------------------------------
// useCreateStoryContent
// ---------------------------------------------------------------------------

export type CreateStoryContentInput = {
  isTemplate: boolean;
  entityId: number;
  copyToTemplate?: boolean;
  createFromTemplate: boolean;
  entitySubdomain: string;
  story: IStory;
  storyContent: IStoryContent;
  storyContents?: IStoryContent[];
  userId?: string;
  order?: number;
  entityName?: string;
  settings?: any;
};

export function useCreateStoryContent() {
  const queryClient = useQueryClient();
  return useMutation<IStoryContent, Error, CreateStoryContentInput>({
    mutationFn: async ({ isTemplate, entityId, story, storyContent, order, entitySubdomain, settings, createFromTemplate }) => {
      const createPayload: any = {
        [isTemplate ? 'storyTemplateId' : 'storyId']: story._id,
        title: storyContent.title,
        description: storyContent.description,
        content: storyContent.content,
        contentHtml: storyContent.content,
        group: storyContent.group,
        format: storyContent.format,
        type: storyContent.type,
        order: order ?? (storyContent as any).order,
        active: true,
        authorId: story.author._id,
        entityId,
      };

      const createRes = await transport
        .url('/api/data/objects/stories/v1/graphql')
        .json({ query: isTemplate ? updateTemplateContent : updateStoryContentQuery, variables: { storyContentData: createPayload } })
        .post()
        .json();

      if (!createRes.data) return Promise.reject(createRes.errors ?? createRes);

      let newContent = processStoryContentOrTemplateContent(createRes.data.contents);
      // Apply branding (incl. body-font color) to newly created sections, matching main.
      // Use entitySubdomain (the value AddContent actually passes) as the entity name —
      // the prior `entityName` param was never supplied, so branding was silently skipped.
      if (!isTemplate && !createFromTemplate && settings && entitySubdomain) {
        newContent = applyBrandingSettings(newContent, settings, entitySubdomain);
      }
      newContent = updateStoryContentId(newContent);

      const updatePayload: any = {
        _id: newContent._id,
        [isTemplate ? 'storyTemplateId' : 'storyId']: story._id,
        title: newContent.title,
        description: newContent.description,
        content: newContent.content,
        contentHtml: newContent.content,
        group: newContent.group,
        format: newContent.format,
        type: newContent.type,
        order: newContent.order,
        active: newContent.active,
        entityId: newContent.entity_id,
        authorId: newContent.author._id,
      };

      const updateRes = await transport
        .url('/api/data/objects/stories/v1/graphql')
        .json({ query: isTemplate ? updateTemplateContent : updateStoryContentQuery, variables: { storyContentData: updatePayload } })
        .post()
        .json();

      if (!updateRes.data) return Promise.reject(updateRes.errors ?? updateRes);

      return processStoryContentOrTemplateContent(updateRes.data.contents) as IStoryContent;
    },
    onSuccess: (newContent, { story, isTemplate, entityId }) => {
      queryClient.setQueryData<IStoryContent[]>(
        storyViewQueryKeys.storyContents({ storyId: story._id, isTemplate, entityId }),
        (prev = []) => {
          const without = prev.filter(sc => sc._id !== newContent._id);
          return orderStoryContents(sortStoryContents([...without, newContent]));
        }
      );
    },
  });
}

// ---------------------------------------------------------------------------
// useDeleteStory
// Navigation (storyList / publicationList / publicationView) handled inline
// ---------------------------------------------------------------------------

export type DeleteStoryInput = {
  isTemplate: boolean;
  entitySubdomain: string;
  story: IStory;
  activeTab: string;
  publication?: IPublication;
};

export type DeleteStoryToasts = {
  successToast: { title: string; body?: string };
  errorToast: { title: string; body?: string };
};

export function useDeleteStory(toasts?: DeleteStoryToasts) {
  const queryClient = useQueryClient();
  const { toggleToast } = useToast();
  return useMutation<unknown, Error, DeleteStoryInput>({
    mutationFn: async ({ isTemplate, entitySubdomain, story, activeTab, publication }) => {
      if (story.state === 'published') {
        void transport.url(`/api/stories/v1/stories/remove`).json({ story }).post();
      }
      const res = await transport
        .url(`/api/data/objects/stories/v1/graphql`)
        .json({ query: isTemplate ? removeTemplate : removeStory, variables: { id: story._id } })
        .post()
        .json();
      if (!res.data) return Promise.reject(res.errors);
      if (toasts?.successToast) {
        toggleToast({ title: toasts.successToast.title, body: toasts.successToast.body, severity: 'success' });
      }
      if (activeTab === TAB_PUBLICATIONS) {
        publication?.id
          ? navigateToPublicationView(entitySubdomain, publication.id)
          : navigateToPublicationList(entitySubdomain);
      } else {
        navigateToStoryList(entitySubdomain);
      }
      return res;
    },
    onSuccess: () => {
      // Drop deleted story from publication list/hierarchy cache.
      void queryClient.invalidateQueries({ queryKey: [...publicationViewQueryKeys.all, 'stories'] });
      void queryClient.invalidateQueries({ queryKey: [...publicationViewQueryKeys.all, 'foldersHierarchy'] });
    },
    onError: () => {
      if (toasts?.errorToast) {
        toggleToast({ title: toasts.errorToast.title, body: toasts.errorToast.body, severity: 'error' });
      }
    }
  });
}

// ---------------------------------------------------------------------------
// useCopyStory
// ---------------------------------------------------------------------------

export type CopyStoryInput = {
  entityId: number;
  entitySubdomain: string;
  storyId: string;
  destinationFolderId: string;
  storyTitle?: string;
  publicationId?: string;
  messages: {
    storyCopied: string;
    storyCopySuccess: string;
    storyCopyFailed: string;
    somethingWentWrong: string;
  };
};

export function useCopyStory() {
  const { featureFlags } = useFeatureFlagStore();
  const { toggleToast } = useToast();

  return useMutation<void, Error, CopyStoryInput>({
    mutationFn: async ({ entityId, entitySubdomain, storyId, destinationFolderId, storyTitle, publicationId, messages }) => {
      const canUseV3Embeds = featureFlags.get('product-feature-reporting-embed-v3');
      const { data } = await transport
        .url(`/api/stories/v1/stories/${storyId}/copy`)
        .json({ entity_id: entityId, entity_subdomain: entitySubdomain, destination_folder_id: destinationFolderId, story_title: storyTitle })
        .post()
        .json<{ data: IStory }>();
      if (canUseV3Embeds) {
        await transport
          .url(`/api/embed_service/v3/embeds/migrate-story`)
          .json({ entityId, storyId: data._id })
          .post()
          .json()
          .catch(() => { });
      }
      toggleToast({ title: messages.storyCopied, body: messages.storyCopySuccess, severity: 'success' });
      if (!publicationId) {
        navigateToFolderList(entitySubdomain, destinationFolderId);
      } else {
        navigateToPublicationFolderList(entitySubdomain, publicationId, destinationFolderId);
      }
    },
    onError: (_error, variables) => {
      toggleToast({
        title: variables.messages.somethingWentWrong,
        body: variables.messages.storyCopyFailed,
        severity: 'error',
      });
    },
  });
}

// ---------------------------------------------------------------------------
// useCopyToTemplate
// ---------------------------------------------------------------------------

export function useCopyToTemplate() {
  const { toggleToast } = useToast();

  return useMutation<void, Error, CopyToTemplateParams>({
    mutationFn: async ({ entityId, entitySubdomain, userId, title, description, storyContents, messages, settings, paddingTop, paddingBottom, customCssCode }) => {
      let template: IStory | null = null;
      try {
        template = await createTemplateFromStory(
          { entityId, title, state: 'draft', description, paddingTop, paddingBottom, customCssCode },
          userId
        );
        const results = await Promise.all(
          storyContents.map((sc) =>
            createTemplateStoryContent({
              isTemplate: true,
              entityId,
              story: template as IStory,
              storyContent: sc,
              settings,
            })
          )
        );
        if (results.some((r) => r instanceof Error)) throw new Error('Content creation failed');
        void transport
          .url(`/api/stories/v1/stories/${template._id}/thumbnail?template=true&entitySubdomain=${entitySubdomain}`)
          .get();
        void SegmentEffects.track({ eventName: 'Stories: Story: Copy to Template', props: template }).catch(() => null);
        toggleToast({ title: messages.templateSaved, body: messages.storyCopiedToTemplates, severity: 'success' });
      } catch (err) {
        if (template) {
          // Best-effort cleanup of the partially-created template.
          void transport
            .url(`/api/data/objects/stories/v1/graphql`)
            .json({ query: removeTemplate, variables: { id: template._id } })
            .post()
            .json()
            .catch(() => null);
        }
        toggleToast({ title: messages.storyNotCopiedToTemplates, body: messages.templateNotSaved, severity: 'error' });
        throw err;
      }
    },
  });
}

/**
 * Create a single template story-content row (used by useCopyToTemplate).
 * Mirrors the create-then-update flow of useCreateStoryContent for templates.
 */
async function createTemplateStoryContent({
  entityId,
  story,
  storyContent,
  settings,
}: {
  isTemplate: true;
  entityId: number;
  story: IStory;
  storyContent: IStoryContent;
  settings?: any;
}): Promise<IStoryContent> {
  const createPayload: any = {
    storyTemplateId: story._id,
    title: storyContent.title,
    description: storyContent.description,
    content: storyContent.content,
    contentHtml: storyContent.content,
    group: storyContent.group,
    format: storyContent.format,
    type: storyContent.type,
    order: (storyContent as any).order,
    active: true,
    authorId: story.author._id,
    entityId,
  };

  const createRes = await transport
    .url('/api/data/objects/stories/v1/graphql')
    .json({ query: updateTemplateContent, variables: { storyContentData: createPayload } })
    .post()
    .json();
  if (!createRes.data) return Promise.reject(createRes.errors ?? createRes);

  let newContent = processStoryContentOrTemplateContent(createRes.data.contents);
  newContent = updateStoryContentId(newContent);
  // settings intentionally not branded for templates (copyToTemplate === true).
  void settings;

  const updatePayload: any = {
    _id: newContent._id,
    storyTemplateId: story._id,
    title: newContent.title,
    description: newContent.description,
    content: newContent.content,
    contentHtml: newContent.content,
    group: newContent.group,
    format: newContent.format,
    type: newContent.type,
    order: newContent.order,
    active: newContent.active,
    entityId: newContent.entity_id,
    authorId: newContent.author._id,
  };

  const updateRes = await transport
    .url('/api/data/objects/stories/v1/graphql')
    .json({ query: updateTemplateContent, variables: { storyContentData: updatePayload } })
    .post()
    .json();
  if (!updateRes.data) return Promise.reject(updateRes.errors ?? updateRes);

  return processStoryContentOrTemplateContent(updateRes.data.contents) as IStoryContent;
}

// ---------------------------------------------------------------------------
// useRemoveBudgetAndProposalsInStory
// ---------------------------------------------------------------------------

export type RemoveBudgetAndProposalsInput = {
  storyId: string;
  proposalId?: number;
};

export function useRemoveBudgetAndProposalsInStory() {
  return useMutation<unknown, Error, RemoveBudgetAndProposalsInput>({
    mutationFn: ({ storyId }) =>
      postGqlQuery(removeAllBudgetProposalsInStoryGql, { storyId })
  });
}

// ---------------------------------------------------------------------------
// useRemoveProposalsInStory
// Returns { mergeTags } so onSuccess can pass mergeTags to caller.
// ---------------------------------------------------------------------------

export type RemoveProposalsInput = {
  storyId: string;
  proposalId: number;
  entityId: number;
};

export function useRemoveProposalsInStory() {
  return useMutation<{ mergeTags: unknown }, Error, RemoveProposalsInput>({
    mutationFn: async ({ storyId, proposalId, entityId }) => {
      await postGqlQuery(removeBudgetAndProposalsInStoryGql, { proposalId, storyId });
      return fetchProposalsInStory(storyId, entityId);
    }
  });
}

// ---------------------------------------------------------------------------
// useExportStory
// isExporting source: mutation.isLoading; isExportingFailure source: isError
// ---------------------------------------------------------------------------

export function useExportStory() {
  const { featureFlags } = useFeatureFlagStore();
  return useMutation<void, Error, IExportStoryProps>({
    mutationFn: async ({
      displayDate,
      displayPageNumber,
      startingPage,
      displayTitle,
      enlargeFonts,
      hasGallery,
      settings,
      story,
      storyContents,
      storyMarginsEnabled,
      margins,
      showHeader,
      showFooter
    }) => {
      const { title, description, thumbnail } = story;
      const { brand: { fonts }, advanced } = settings;
      const hasFootNotes = displayDate || displayTitle || displayPageNumber;

      let sortedStoryContents = filterAndOrderStoryContents(storyContents, {
        hideHeader: !showHeader,
        hideFooter: !showFooter
      });

      const canAddProposalsContent = featureFlags.get(FLAG_ADD_PROPOSALS_TO_STORIES) as boolean;

      if (canAddProposalsContent) {
        const result = await transport
          .url('/api/stories/v1/stories/getProposalsInStory')
          .json({ story_id: story._id, entity_id: story.entity_id })
          .post()
          .json<{ mergeTags?: any }>();
        sortedStoryContents = replaceStoryContentsWithMergeTagValues(sortedStoryContents, result?.mergeTags ?? {});
      }

      const sortedStoryContentsWithDisabledClick = disableClickFromStoryContentWithEmbedV3(sortedStoryContents);
      const progressTrackerSettings = await fetchProgressTrackerSettings(story.entity_id);

      const html = generateBase64HTML({
        id: story._id,
        storyContents: sortedStoryContentsWithDisabledClick,
        title,
        description,
        hasGallery,
        thumbnail,
        options: {
          pdf: true,
          gaTrackingCode: advanced?.google_analytics_id,
          noJS: true
        },
        fonts,
        margins: margins as any,
        storyMarginsEnabled,
        hasFootNotes,
        storyCustomCss: story.customCssCode,
        publicationCustomCss: (story as any).folder?.publication?.customCssCode,
        isFollowable: story.isFollowable,
        progressTrackerSettings
      });

      const blob = await transport
        .url('/api/content_export_service/v1/pdf')
        .json({
          showMargins: !storyMarginsEnabled,
          htmlEncoded: html,
          displayDate,
          displayPageNumber,
          startingPage,
          displayTitle,
          enlargeFonts
        })
        .post()
        .json<{ pdf: string }>();

      void SegmentEffects.track({ eventName: 'Stories: Story: Exported to PDF', props: story }).catch(() => null);
      downloadPdfFromBlob(Buffer.from(blob.pdf, 'utf-8'), title);
    }
  });
}

// ---------------------------------------------------------------------------
// Proposal mutation helpers
// Each returns { mergeTags } so caller can pass to useUpdateAllStoryContentsMergeTagsDB.
// Caller is responsible for invalidating ['storyView', 'proposals'] query.
// ---------------------------------------------------------------------------

const postGqlQuery = (query: string, variables: Record<string, unknown>): Promise<any> =>
  transport
    .url('/api/data/objects/stories/v1/graphql')
    .json({ query, variables })
    .post()
    .json()
    .then((res: { data?: unknown; errors?: unknown[] }) => {
      if (res.data) return res.data;
      throw new Error(JSON.stringify(res.errors ?? res));
    });

const fetchProposalsInStory = (storyId: string, entityId: number): Promise<{ mergeTags: IMergeTags }> =>
  transport
    .url('/api/stories/v1/stories/getProposalsInStory')
    .json({ story_id: storyId, entity_id: entityId })
    .post()
    .json<{ mergeTags: IMergeTags }>();

export type AddProposalToStoryInput = {
  budgetId: number;
  proposalId: number;
  storyId: string;
  entityId: number;
};

export function useAddProposalToStory() {
  return useMutation<{ mergeTags: IMergeTags }, Error, AddProposalToStoryInput>({
    mutationFn: async ({ budgetId, proposalId, storyId, entityId }) => {
      await postGqlQuery(addProposalToStoryGql, { objects: { budgetId, proposalId, storyId } });
      return fetchProposalsInStory(storyId, entityId);
    }
  });
}

export type ReplaceProposalInStoryInput = {
  id: string;
  proposalId: number;
  storyId: string;
  entityId: number;
};

export function useReplaceProposalInStory() {
  return useMutation<{ mergeTags: IMergeTags }, Error, ReplaceProposalInStoryInput>({
    mutationFn: async ({ id, proposalId, storyId, entityId }) => {
      await postGqlQuery(replaceProposalInStoryGql, { id, proposalId });
      return fetchProposalsInStory(storyId, entityId);
    }
  });
}

export type ReplaceBudgetInStoryInput = {
  budgetId: number;
  proposalIds: number[];
  storyId: string;
  entityId: number;
};

export function useReplaceBudgetInStory() {
  return useMutation<{ mergeTags: IMergeTags }, Error, ReplaceBudgetInStoryInput>({
    mutationFn: async ({ budgetId, proposalIds, storyId, entityId }) => {
      await postGqlQuery(removeBudgetAndProposalsInStoryGql, { proposalId: null, storyId });
      await Promise.all(
        proposalIds.map((proposalId) =>
          postGqlQuery(addProposalToStoryGql, { objects: { budgetId, proposalId, storyId } })
        )
      );
      return fetchProposalsInStory(storyId, entityId);
    }
  });
}
